import React from "react";

type SkeletonRadius = "none" | "sm" | "md" | "lg" | "full";

const RADIUS_CLASS: Record<SkeletonRadius, string> = {
  none: "",
  sm: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

interface SkeletonProps {
  // Tailwind sizing/spacing classes, e.g. "h-4 w-1/2"
  className?: string;
  radius?: SkeletonRadius;
  // Inline width/height for sizes Tailwind can't express (e.g. a computed pixel value)
  width?: number | string;
  height?: number | string;
}

// Size it to match the real element so nothing shifts when data lands — the point over a spinner
export const Skeleton: React.FC<SkeletonProps> = ({
  className = "",
  radius = "sm",
  width,
  height,
}) => (
  <div
    aria-hidden="true"
    style={{ width, height }}
    // motion-reduce disables the pulse for users who ask for less animation
    className={`bg-zinc-200 animate-pulse motion-reduce:animate-none ${RADIUS_CLASS[radius]} ${className}`}
  />
);

interface SkeletonTextProps {
  // Number of lines to render; the last one is shortened so it reads like real copy
  lines?: number;
  className?: string;
  lineClassName?: string;
}

// Multi-line text placeholder.
export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 2,
  className = "",
  lineClassName = "h-3.5",
}) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={`${lineClassName} ${i === lines - 1 && lines > 1 ? "w-2/3" : "w-full"}`}
      />
    ))}
  </div>
);

interface SkeletonGroupProps {
  // How many copies of `children` to render — for card grids and list rows
  count: number;
  children: React.ReactNode;
  // Announced to screen readers while the placeholders are on screen
  label?: string;
  className?: string;
}

// Repeats a placeholder and owns the one loading announcement, so screen readers hear it once
export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({
  count,
  children,
  label = "Loading",
  className = "",
}) => (
  <div role="status" aria-label={label} className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <React.Fragment key={i}>{children}</React.Fragment>
    ))}
  </div>
);

// Fixed silhouette rather than random heights, so the placeholder doesn't reshuffle on re-render
const CHART_BAR_HEIGHTS = [
  42, 61, 35, 78, 55, 88, 47, 70, 33, 64, 92, 51, 39, 75, 58, 84, 44, 67, 30, 72, 53, 86, 40, 62,
];

interface SkeletonChartProps {
  className?: string;
  // Trimmed from the front of the silhouette — fewer columns for a narrow panel
  bars?: number;
  label?: string;
  // The control row above the plot; drop it where the real controls live outside the panel
  withHeader?: boolean;
}

// Chart placeholder: control row, y-axis ticks, then a plot area of varying-height columns
export const SkeletonChart: React.FC<SkeletonChartProps> = ({
  className = "",
  bars = CHART_BAR_HEIGHTS.length,
  label = "Loading chart",
  withHeader = true,
}) => (
  <div
    role="status"
    aria-label={label}
    className={`flex h-full min-h-[240px] w-full flex-col gap-4 p-4 ${className}`}
  >
    {withHeader && (
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-eai-control-sm w-32" radius="md" />
        <Skeleton className="h-eai-control-sm w-24" radius="md" />
        <Skeleton className="ml-auto h-eai-control-sm w-20" radius="md" />
      </div>
    )}

    <div className="flex min-h-0 flex-1 gap-3">
      <div className="flex w-9 shrink-0 flex-col justify-between py-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-2.5 w-full" />
        ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 items-end gap-1.5 border-b border-l border-eai-border px-2">
          {CHART_BAR_HEIGHTS.slice(0, bars).map((height, i) => (
            <Skeleton key={i} className="min-w-0 flex-1" height={`${height}%`} />
          ))}
        </div>
        <div className="mt-2 flex justify-between pl-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-2.5 w-10" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default Skeleton;
